/**
 * NotificationBell — rebuilt Feb 2026 (iter_338)
 *
 * Modeled on Linear / Notion / GitHub notification patterns:
 *   • Tabs — All · Unread · Requests (actionable)
 *   • Lucide icons per type + a distinct accent color per category
 *   • Time-bucket headers — Today · Yesterday · Last 7 days · Older
 *   • Same-type grouping — "3 new Website Leads" instead of 3 separate rows
 *   • Inline Approve / Reject on job_approval_request + submission_request
 *   • Load-more pagination + type-filter dropdown
 *   • Auto-refresh on window focus (in addition to the 30s poll) so
 *     switching tabs / apps pulls new notifications instantly
 *   • Resolved rows visually distinct (dimmed, green ✓)
 *   • Zero backend contract changes — reuses /notifications endpoints
 *
 * Performance notes:
 *   • Groupings are computed via useMemo on the fetched slice only —
 *     capped at 50 items per page, so O(n) traversal is <5ms.
 *   • Polling stays at 30s. Focus-refresh is debounced to at most 1 hit
 *     per 15s so tab-cycling doesn't hammer the endpoint.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { formatDate } from '@/lib/dates';
import { useNavigate } from 'react-router-dom';
import {
  Bell, Check, CheckCheck, X, Filter, Loader2,
  ClipboardList, CheckCircle2, XCircle, Send, MessageSquare,
  UserPlus, Sparkles, AlertCircle, FileText, Users, Briefcase,
  Building2, ChevronDown, Inbox, ArrowRight, Settings2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';

// ── Icon + accent lookup per notification type ─────────────────────────
// Keep the map small; unknown types fall back to Bell + slate.
const TYPE_META = {
  job_approval_request:   { icon: ClipboardList,  accent: 'text-amber-400',   bar: 'border-l-amber-500',   label: 'Job Approval' },
  job_approved:           { icon: CheckCircle2,   accent: 'text-emerald-400', bar: 'border-l-emerald-500', label: 'Job Approved' },
  job_rejected:           { icon: XCircle,        accent: 'text-red-400',     bar: 'border-l-red-500',     label: 'Job Rejected' },
  submission_request:     { icon: Send,           accent: 'text-sky-400',     bar: 'border-l-sky-500',     label: 'Submission Request' },
  submission_approved:    { icon: CheckCircle2,   accent: 'text-emerald-400', bar: 'border-l-emerald-500', label: 'Submission Approved' },
  submission_rejected:    { icon: XCircle,        accent: 'text-red-400',     bar: 'border-l-red-500',     label: 'Submission Rejected' },
  client_query:           { icon: MessageSquare,  accent: 'text-violet-400',  bar: 'border-l-violet-500',  label: 'Client Query' },
  website_lead:           { icon: Sparkles,       accent: 'text-fuchsia-400', bar: 'border-l-fuchsia-500', label: 'Website Lead' },
  lead_assigned:          { icon: UserPlus,       accent: 'text-sky-400',     bar: 'border-l-sky-500',     label: 'Lead Assigned' },
  candidate_shared:       { icon: Users,          accent: 'text-blue-400',    bar: 'border-l-blue-500',    label: 'Candidate Shared' },
  interview_scheduled:    { icon: Briefcase,      accent: 'text-purple-400',  bar: 'border-l-purple-500',  label: 'Interview Scheduled' },
  bd_alert:               { icon: Building2,      accent: 'text-amber-400',   bar: 'border-l-amber-500',   label: 'BD Alert' },
  ai_alert:               { icon: Sparkles,       accent: 'text-fuchsia-400', bar: 'border-l-fuchsia-500', label: 'AI Alert' },
  assignment_nudge:       { icon: UserPlus,       accent: 'text-amber-400',   bar: 'border-l-amber-500',   label: 'Unassigned Role' },
  interview_resume_missing: { icon: AlertCircle,  accent: 'text-amber-400',  bar: 'border-l-amber-500',   label: 'Resume Missing' },
};
const DEFAULT_META = { icon: Bell, accent: 'text-slate-400', bar: 'border-l-slate-600', label: 'Notification' };

// Types where the recipient can (or should) take direct action.
// Rendered under the "Requests" tab and get inline Approve/Reject buttons.
const ACTIONABLE_TYPES = new Set(['job_approval_request', 'submission_request']);

const POLL_INTERVAL_MS = 30000;
const FOCUS_REFRESH_MIN_MS = 15000;
const PAGE_SIZE = 25;

// ── time bucketing ────────────────────────────────────────────────────
function startOfLocalDay(offsetDays = 0) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - offsetDays);
  return d.getTime();
}

function bucketOf(createdAt) {
  const t = new Date(createdAt).getTime();
  if (t >= startOfLocalDay(0)) return 'today';
  if (t >= startOfLocalDay(1)) return 'yesterday';
  if (t >= startOfLocalDay(7)) return 'last7';
  return 'older';
}

const BUCKET_LABEL = { today: 'Today', yesterday: 'Yesterday', last7: 'Last 7 days', older: 'Older' };
const BUCKET_ORDER = ['today', 'yesterday', 'last7', 'older'];

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
}

// ── same-type grouping within a bucket ─────────────────────────────────
// Consecutive notifications sharing the same `type` collapse into a single
// row when there are 2+ of them within a single time bucket. First row's
// metadata is used as the "cover"; the individual items are still
// available under an expandable list (keeps a11y + click-through).
function groupConsecutive(items) {
  const out = [];
  let cur = null;
  for (const n of items) {
    if (cur && cur.type === n.type && cur.bucket === bucketOf(n.created_at) &&
        !ACTIONABLE_TYPES.has(n.type)) {
      cur.items.push(n);
      cur.anyUnread = cur.anyUnread || !n.is_read;
    } else {
      if (cur) out.push(cur);
      cur = {
        key: n.id,
        type: n.type,
        bucket: bucketOf(n.created_at),
        cover: n,
        items: [n],
        anyUnread: !n.is_read,
      };
    }
  }
  if (cur) out.push(cur);
  return out;
}

// ── main component ────────────────────────────────────────────────────
// `variant` only suffixes the test ids: the shell renders TWO bells (mobile and
// desktop) and they used to share one data-testid, so a test could not tell
// which of the two it had hold of.
export default function NotificationBell({ variant = '' }) {
  const idSuffix = variant ? `-${variant}` : '';
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('action');   // 'action' | 'updates' | 'all'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' or a specific type
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actingOn, setActingOn] = useState(null); // notif id being acted on

  const prevCountRef = useRef(0);
  const lastFocusFetchRef = useRef(0);
  const panelRef = useRef(null);
  const navigate = useNavigate();

  // Badge count via a SHARED query key: the bell is mounted twice (desktop +
  // mobile header) and StrictMode double-invokes effects, which is why this
  // endpoint fired 4x per page. React Query collapses all of that into one
  // request and keeps the 30s poll (2026-06).
  const { data: countData } = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.get('/notifications/unread-count', { params: { days: 30 } }).then(r => r.data),
    // First paint uses the count that already shipped in the shell payload, so
    // the badge costs zero extra requests on load; the 30s poll keeps it live.
    initialData: () => {
      const boot = queryClient.getQueryData(['bootstrap']);
      return boot ? { count: boot.notifications_unread || 0,
                      updates_count: boot.notifications_updates || 0 } : undefined;
    },
    initialDataUpdatedAt: () => Date.now(),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    retry: 0,
  });
  // The badge counts ONLY what is waiting on this person. Updates are kept and
  // listed, they just do not light the bell (owner, 6 Jun 2026: "reduce unread
  // pings so the bell only lights up for things needing action").
  const unreadCount = countData?.count ?? 0;
  const updatesCount = countData?.updates_count ?? 0;
  const setUnread = (next) => queryClient.setQueryData(['notifications-unread-count'],
    old => ({ ...(old || {}), ...next }));
  const bumpUnread = (d, isAction = true) => queryClient.setQueryData(['notifications-unread-count'],
    old => {
      const key = isAction ? 'count' : 'updates_count';
      return { ...(old || {}), [key]: Math.max(0, (old?.[key] || 0) + d) };
    });
  useEffect(() => {
    if (unreadCount > prevCountRef.current && prevCountRef.current !== 0) {
      toast.info('You have new notifications', { duration: 3000 });
    }
    prevCountRef.current = unreadCount;
  }, [unreadCount]);
  const fetchUnreadCount = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
  }, []);

  // Fetch the notification list. offset=0 replaces; otherwise appends.
  // `bucket` is server-side, so "Needs you" is the true list and not whatever
  // happened to be in the newest 25 rows.
  const fetchNotifications = useCallback(async (offset = 0, bucket = 'action') => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = { limit: PAGE_SIZE, offset, days: 30, bucket };
      const res = await api.get('/notifications', { params });
      const fresh = res.data.notifications || [];
      setHasMore((offset + fresh.length) < (res.data.total || 0));
      setNotifications(prev => offset === 0 ? fresh : [...prev, ...fresh]);
    } catch { /* silent */ }
    setLoading(false);
    setLoadingMore(false);
  }, []);

  // Periodic badge poll is handled by the shared query above.
  useEffect(() => {
    const onFocus = () => {
      const now = Date.now();
      if (now - lastFocusFetchRef.current < FOCUS_REFRESH_MIN_MS) return;
      lastFocusFetchRef.current = now;
      fetchUnreadCount();
      if (open) fetchNotifications(0, tab);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, tab, fetchUnreadCount, fetchNotifications]);

  // Panel open → land on whichever shelf actually has something, then load it.
  useEffect(() => {
    if (!open) return;
    if (unreadCount === 0 && updatesCount > 0 && tab === 'action') setTab('updates');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (open) fetchNotifications(0, tab);
  }, [open, tab, fetchNotifications]);

  // Outside click close — using `click` (not mousedown) so it fires
  // AFTER any button onClick, avoiding a race where opening + immediate
  // outside-close cancel each other on the same mouse gesture.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    // Small delay so the click that opened the panel doesn't immediately close it.
    const t = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', handler); };
  }, [open]);

  // ── local state updates ────────────────────────────────────────────
  const markOneRead = async (n) => {
    const id = typeof n === 'string' ? n : n.id;
    const isAction = typeof n === 'string' ? true : n.action_needed !== false;
    setNotifications(prev => prev.map(x => x.id === id ? { ...x, is_read: true } : x));
    bumpUnread(-1, isAction);
    if (isAction) prevCountRef.current = Math.max(0, prevCountRef.current - 1);
    try { await api.post('/notifications/mark-read', { notification_ids: [id] }); }
    catch { /* silent — UI already reflects the intent */ }
  };

  const markAllRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnread({ count: 0, updates_count: 0 });
    prevCountRef.current = 0;
    try { await api.post('/notifications/mark-read', { all: true }); toast.success('All marked as read'); }
    catch { toast.error('Failed to sync — will retry on next open'); }
  };

  // Inline action on a job_approval / submission_request notification.
  // Uses the existing type-specific endpoints; falls back to the generic
  // notifications/{id}/resolve if the type isn't specially wired.
  const handleAction = async (n, action) => {
    setActingOn(n.id);
    const notifId = n.id;
    const meta = n.metadata || {};
    try {
      if (n.type === 'job_approval_request' && meta.job_id) {
        const path = action === 'approve' ? `/jobs/${meta.job_id}/approve` : `/jobs/${meta.job_id}/reject`;
        await api.post(path, {});
      } else if (n.type === 'submission_request' && meta.submission_id) {
        const path = action === 'approve' ? `/submissions/${meta.submission_id}/approve` : `/submissions/${meta.submission_id}/reject`;
        await api.post(path, {});
      } else {
        await api.post(`/notifications/${notifId}/resolve`, { resolution: action });
      }
      // Optimistically update — set resolved + read.
      setNotifications(prev => prev.map(x =>
        x.id === notifId ? { ...x, is_read: true, resolution: action, resolved_at: new Date().toISOString() } : x
      ));
      bumpUnread(n.is_read ? 0 : -1, n.action_needed !== false);
      toast.success(action === 'approve' ? 'Approved' : 'Rejected');
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    } finally {
      setActingOn(null);
    }
  };

  const handleRowClick = (n) => {
    if (!n.is_read) markOneRead(n);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  // ── derived: type-filtered + grouped (the bucket is applied server-side) ──
  const availableTypes = useMemo(() => {
    const s = new Set(notifications.map(n => n.type));
    return Array.from(s).sort();
  }, [notifications]);

  const filtered = useMemo(() => {
    let list = notifications;
    if (typeFilter !== 'all') list = list.filter(n => n.type === typeFilter);
    return list;
  }, [notifications, typeFilter]);

  const bucketed = useMemo(() => {
    const grouped = groupConsecutive(filtered);
    const buckets = { today: [], yesterday: [], last7: [], older: [] };
    for (const g of grouped) buckets[g.bucket].push(g);
    return buckets;
  }, [filtered]);

  // The dropdown is a glance: 5 groups, then "View all". A max-height plus
  // native scrolling is the safety net — never Radix ScrollArea here, it sizes
  // its viewport to content and simply got clipped.
  const PANEL_LIMIT = 5;
  const visibleBuckets = useMemo(() => {
    const out = { today: [], yesterday: [], last7: [], older: [] };
    let budget = PANEL_LIMIT;
    for (const b of BUCKET_ORDER) {
      const take = Math.max(0, Math.min(budget, bucketed[b].length));
      out[b] = bucketed[b].slice(0, take);
      budget -= take;
    }
    return out;
  }, [bucketed]);

  const bucketCounts = useMemo(() => ({
    action: unreadCount,
    updates: updatesCount,
  }), [unreadCount, updatesCount]);

  return (
    <div className="relative flex-shrink-0" ref={panelRef} data-testid={`notification-bell-container${idSuffix}`}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(prev => !prev); }}
        className="relative flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
        data-testid={`notification-bell-btn${idSuffix}`}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 ? (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-xs font-bold text-white bg-sky-500 rounded-full px-1"
            data-testid="notification-unread-badge"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : updatesCount > 0 ? (
          /* Nothing is waiting on you, but there is something new to read. A dot
             says so without pretending it is urgent. */
          <span
            className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-slate-500"
            data-testid="notification-updates-dot"
          />
        ) : null}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[420px] max-w-[calc(100vw-24px)] bg-charcoal border border-white/10 rounded-xl shadow-2xl z-[100] overflow-hidden flex flex-col"
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-xs font-semibold text-sky-400 bg-sky-500/10 border border-sky-500/20 rounded px-1 py-1">
                  {unreadCount} need you
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setOpen(false); navigate('/app/profile#notifications'); }}
                className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
                data-testid="notification-prefs-btn"
                aria-label="Choose what you get notified about"
                title="Choose what you get notified about"
              >
                <Settings2 className="w-3.5 h-3.5" />
              </button>
              {/* Filter by type */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
                    data-testid="notification-type-filter-btn"
                    aria-label="Filter by type"
                    title="Filter by type"
                  >
                    <Filter className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-charcoal border-white/10 w-52">
                  <DropdownMenuLabel className="text-xs text-slate-400">Filter by type</DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-white/[0.06]" />
                  <DropdownMenuCheckboxItem
                    checked={typeFilter === 'all'}
                    onCheckedChange={() => setTypeFilter('all')}
                    className="text-xs text-slate-300"
                  >All types</DropdownMenuCheckboxItem>
                  {availableTypes.map(t => (
                    <DropdownMenuCheckboxItem
                      key={t}
                      checked={typeFilter === t}
                      onCheckedChange={() => setTypeFilter(typeFilter === t ? 'all' : t)}
                      className="text-xs text-slate-300"
                    >{(TYPE_META[t] || DEFAULT_META).label}</DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {unreadCount > 0 && (
                <Button
                  variant="ghost" size="sm"
                  onClick={markAllRead}
                  className="text-xs text-slate-400 hover:text-white h-7 px-2"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3.5 h-3.5 mr-1" /> Mark all read
                </Button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-slate-500 hover:text-white transition-colors"
                data-testid="close-notification-panel"
                aria-label="Close notifications"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-1 px-2 pt-2 border-b border-white/[0.06] bg-white/[0.02]">
            {[
              { key: 'action',  label: 'Needs you', count: bucketCounts.action },
              { key: 'updates', label: 'Updates',   count: bucketCounts.updates },
              { key: 'all',     label: 'All',       count: 0 },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`notif-tab-${t.key}`}
                className={`relative px-3 py-1 text-xs font-medium rounded-t-md transition-colors ${
                  tab === t.key
                    ? 'text-white bg-white/[0.06] border-b-2 border-sky-500 -mb-px'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                {t.label}
                {t.count > 0 && (
                  <span className={`ml-1 text-xs font-semibold rounded px-1 ${
                    tab === t.key ? 'bg-sky-500/20 text-sky-300' : 'bg-white/[0.06] text-slate-400'
                  }`}>{t.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Body — a glance, not an inbox: the newest 8 groups, no scrolling.
              Anything more belongs on the Notifications page, which has room to
              filter, scroll and act in bulk. (The old ScrollArea also could not
              scroll: Radix sizes its viewport to content, so `flex-1 max-h` on
              the root simply clipped it.) */}
          <div className="max-h-[60vh] overflow-y-auto overscroll-contain">
            {loading && notifications.length === 0 ? (
              <div className="p-8 flex items-center justify-center">
                <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState tab={tab} typeFilter={typeFilter} />
            ) : (
              <div>
                {BUCKET_ORDER.map(b => (
                  visibleBuckets[b].length > 0 && (
                    <div key={b} data-testid={`notif-bucket-${b}`}>
                      <div className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                        {BUCKET_LABEL[b]}
                      </div>
                      <div className="divide-y divide-white/[0.04]">
                        {visibleBuckets[b].map(g => (
                          <NotificationGroup
                            key={g.key}
                            group={g}
                            onRowClick={handleRowClick}
                            onMarkRead={markOneRead}
                            onAction={handleAction}
                            actingOn={actingOn}
                          />
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>

          {/* Footer — always there, so "where is the rest" is never a question */}
          <button
            onClick={() => { setOpen(false); navigate('/app/notifications'); }}
            data-testid="notif-view-all-btn"
            className="flex items-center justify-center gap-1 w-full px-4 py-2 text-xs font-medium text-slate-300 hover:text-white border-t border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors"
          >
            View all notifications
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// ── sub components ────────────────────────────────────────────────────

function EmptyState({ tab, typeFilter }) {
  const [msg, hint] = (() => {
    if (typeFilter !== 'all') return ['No notifications match this filter.', 'Try a different type from the funnel icon above.'];
    if (tab === 'action')  return ['Nothing is waiting on you.', 'Approvals, replies and failures that need a decision land here.'];
    if (tab === 'updates') return ['No new updates.', 'Stage moves, digests and FYIs collect here without pinging you.'];
    return ['No notifications yet.', 'When something needs your attention it will appear here.'];
  })();
  return (
    <div className="p-8 text-center" data-testid="no-notifications">
      <div className="w-10 h-10 rounded-full bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mx-auto mb-3">
        <Inbox className="w-4 h-4 text-slate-500" />
      </div>
      <p className="text-sm text-slate-300 font-medium">{msg}</p>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </div>
  );
}

function NotificationGroup({ group, onRowClick, onMarkRead, onAction, actingOn }) {
  const [expanded, setExpanded] = useState(false);
  const { cover, items, anyUnread, type } = group;
  const meta = TYPE_META[type] || DEFAULT_META;
  const Icon = meta.icon;

  // Single-item group → render just the row.
  if (items.length === 1) {
    return <NotificationRow n={cover} meta={meta} onRowClick={onRowClick} onMarkRead={onMarkRead} onAction={onAction} actingOn={actingOn} />;
  }

  return (
    <div className={`border-l-2 ${meta.bar} ${anyUnread ? 'bg-white/[0.02]' : ''}`}>
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors"
        data-testid={`notif-group-${type}`}
      >
        <div className="flex items-start gap-3">
          <Icon className={`w-4 h-4 mt-1 ${meta.accent} shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-xs font-semibold truncate ${anyUnread ? 'text-white' : 'text-slate-400'}`}>
                {items.length} new {meta.label}s
              </p>
              {anyUnread && <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Most recent: {cover.title || cover.message?.slice(0, 60) || '—'}
            </p>
            <p className="text-xs text-slate-600 mt-1 flex items-center gap-2">
              {timeAgo(cover.created_at)}
              <span className="text-slate-500">· {expanded ? 'Hide' : 'Show all'}</span>
            </p>
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-white/[0.04] bg-black/20">
          {items.map(n => (
            <NotificationRow key={n.id} n={n} meta={meta}
              onRowClick={onRowClick} onMarkRead={onMarkRead}
              onAction={onAction} actingOn={actingOn}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, meta, onRowClick, onMarkRead, onAction, actingOn, nested = false }) {
  const Icon = meta.icon;
  const isResolved = !!n.resolution;
  const canAct = ACTIONABLE_TYPES.has(n.type) && !isResolved;
  return (
    <div
      className={`text-left w-full px-4 py-3 hover:bg-white/[0.03] transition-colors ${!nested ? `border-l-2 ${meta.bar}` : ''} ${
        !n.is_read && !nested ? 'bg-white/[0.03]' : ''
      } ${isResolved ? 'opacity-70' : ''}`}
      data-testid={`notification-item-${n.id}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={() => onRowClick(n)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onRowClick(n); }}
        className="cursor-pointer"
      >
        <div className="flex items-start gap-3">
          <Icon className={`w-4 h-4 mt-1 ${meta.accent} shrink-0`} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className={`text-xs font-semibold truncate ${!n.is_read ? 'text-white' : 'text-slate-400'}`}>
                {n.title}
              </p>
              {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-sky-500 shrink-0" />}
              {isResolved && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider rounded px-1 py-1 border ${
                  n.resolution === 'approved' || n.resolution === 'approve'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : n.resolution === 'rejected' || n.resolution === 'reject'
                      ? 'bg-red-500/10 text-red-400 border-red-500/20'
                      : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                }`}>
                  <CheckCircle2 className="w-2.5 h-2.5" />
                  {n.resolution}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{n.message}</p>
            <p className="text-xs text-slate-600 mt-1">{timeAgo(n.created_at)}</p>
          </div>
          {!n.is_read && (
            <button
              onClick={(e) => { e.stopPropagation(); onMarkRead(n); }}
              className="p-1 text-slate-600 hover:text-sky-500 transition-colors shrink-0"
              title="Mark as read"
              aria-label="Mark as read"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Inline actions — Approve / Reject on actionable types */}
      {canAct && (
        <div className="mt-2 ml-6 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            onClick={() => onAction(n, 'approve')}
            disabled={actingOn === n.id}
            className="h-7 text-xs px-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
            data-testid={`notif-approve-${n.id}`}
          >
            {actingOn === n.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Approve
          </Button>
          <Button
            size="sm"
            onClick={() => onAction(n, 'reject')}
            disabled={actingOn === n.id}
            className="h-7 text-xs px-2 bg-red-500/10 border border-red-500/30 text-red-300 hover:bg-red-500/20"
            data-testid={`notif-reject-${n.id}`}
          >
            <XCircle className="w-3 h-3 mr-1" /> Reject
          </Button>
        </div>
      )}
    </div>
  );
}
